import { LoadingOutlined } from "@ant-design/icons";
import { useProductActions } from "_actions/product.actions";
import { cx, formatCurrency } from "_lib/utils";
import { Breadcrumb, Button, message, Spin } from "antd";
import { TrendingDown, TrendingUp } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

export const ProductsPage = ({ gatewayInstance }: { gatewayInstance: any }) => {
  const productActions = useProductActions();
  const [selectedInvestmentCategory, setSelectedInvestmentCategory] = useState<
    "smallcase" | "equity" | "mutualFunds"
  >("smallcase");
  const [isLoading, setIsLoading] = useState(true);
  const [smallcases, setSmallcases] = useState([]);

  useEffect(() => {
    // Fetch products
    setIsLoading(true);
    productActions
      .discoverSmallcases()
      .then(
        (response) => {
          setSmallcases(response.data.data.smallcases);
        },
        (error) => {
          console.error(error);
          message.error("Failed to fetch Smallcase products");
        }
      )
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const handleBuy = (scid: string) => {
    setIsLoading(true);
    productActions
      .buySmallcase("BUY", scid)
      .then(
        (response) => {
          const transactionId = response.data.data.transactionId;

          gatewayInstance
            .triggerTransaction({
              transactionId: transactionId,
            })
            .then((txnResponse: any) => {
              console.debug("received response:", txnResponse);

              message.success("Successfully bought smallcase");
            })
            .catch((err: any) => {
              console.error("received error:", err.message);
              switch (err.message) {
                case "user_cancelled":
                  message.error(
                    "You have cancelled the transaction. Please try again",
                    3
                  );
                  break;
                case "market_closed":
                  message.error(
                    "Market has closed. Please try again during market hours",
                    3
                  );
                  break;
                case "order_pending":
                  message.error(
                    "Buy order is already in progress. Please try again after sometime",
                    3
                  );
                  break;
                case "already_subscribed":
                  message.error(
                    "You have already subscribed to this smallcase.",
                    3
                  );
                  break;
                default:
                  message.error(
                    "Failed to buy smallcase. Please try again after sometime",
                    3
                  );
                  break;
              }
            });
        },
        (error) => {
          console.error(error);
          message.error("Failed to buy smallcase");
        }
      )
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <section className="mx-auto space-y-5">
      <Breadcrumb
        items={[
          {
            title: <Link to="/">Home</Link>,
          },
          {
            title: "Products",
          },
        ]}
      />
      <div className="flex gap-8 bg-white rounded-lg p-4">
        <a
          className={cx(
            "text-sm font-medium px-3",
            selectedInvestmentCategory == "smallcase"
              ? "font-semibold underline"
              : ""
          )}
          onClick={() => setSelectedInvestmentCategory("smallcase")}
        >
          Smallcase
        </a>
        {/* <a
          className={cx(
            "text-sm font-medium px-3",
            selectedInvestmentCategory == "equity" ? "font-bold underline" : ""
          )}
          onClick={() => setSelectedInvestmentCategory("equity")}
        >
          Equity
        </a>
        <a
          className={cx(
            "text-sm font-medium px-3",
            selectedInvestmentCategory == "mutualFunds"
              ? "font-semibold underline"
              : ""
          )}
          onClick={() => setSelectedInvestmentCategory("mutualFunds")}
        >
          Mutual Funds
        </a> */}
      </div>
      {isLoading && (
        <div className="flex items-center justify-center h-[60vh]">
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      )}
      {selectedInvestmentCategory == "smallcase" && (
        <div className="text-slate-500 text-center space-y-5">
          {smallcases.map((item: any, index: number) => (
            <div
              key={index}
              className="grid lg:grid-cols-6 items-center gap-8 bg-white rounded-lg p-4 hover:text-black"
            >
              <div className="flex gap-4 col-span-2 items-center text-sm font-medium px-3">
                <img
                  src={item.info.imageUrl}
                  alt={item.info.name}
                  width={80}
                  className="w-20 h-20"
                />
                <div className="text-start">
                  <h3 className="text-lg font-semibold pb-2">
                    {item.info.name}
                  </h3>
                  <p className="text-xs">{item.info.shortDescription}</p>
                  {/* <p className="text-xs">{item.info.shortDescription}</p> */}
                </div>
              </div>

              <div className="text-sm font-medium px-3">
                <p>Index Value</p>
                <div className="text-lg font-semibold">
                  {item.stats.indexValue.toFixed(2)}
                </div>
              </div>
              <div className="text-sm font-medium px-3">
                <p>Minimum Investment</p>
                <div className="text-lg font-semibold">
                  {formatCurrency(item.stats.minInvestAmount, 0)}
                </div>
              </div>
              <div className="text-sm font-medium px-3">
                <p>1 Year CAGR</p>
                <div
                  className={cx(
                    "flex justify-center items-end text-lg font-semibold",
                    item.stats.ratios.cagr1y > 0
                      ? "text-green-500"
                      : "text-red-500"
                  )}
                >
                  {item.stats.ratios.cagr1y > 0 ? (
                    <TrendingUp className="w-6 me-1" />
                  ) : (
                    <TrendingDown className="w-6 me-1" />
                  )}
                  {(item.stats.ratios.cagr1y * 100).toFixed(2)}%
                </div>
              </div>

              <div className="text-center">
                <Button
                  type="primary"
                  className="font-bold text-xs px-8"
                  onClick={() => handleBuy(item.scid)}
                >
                  Invest Now
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {selectedInvestmentCategory == "equity" && (
        <p className="text-slate-500 text-center">No products found</p>
      )}
      {selectedInvestmentCategory == "mutualFunds" && (
        <p className="text-slate-500 text-center">No products found</p>
      )}
    </section>
  );
};
